// VariablePalette — textarea + variables popup.
//
// Wraps a single textarea with a "Variables" toggle button. Clicking
// the toggle opens a popup that lists every variable available for
// the active template_type, grouped into "Company" and "Page-specific"
// sections (PRINT_TEMPLATE_PLAN.md §6 Phase 6). Clicking a chip:
//   - copies `{{token}}` to the clipboard, AND
//   - inserts `{{token}}` at the textarea's current cursor position
//     (or selection — replacing the selected text if any).
//
// Drag is supported via HTML5 DnD: each chip is draggable; dropping
// onto the textarea inserts the token at the drop position. Falls
// back to click-to-insert on browsers/touch devices that don't fire
// dragstart.

import { useRef, useState, type TextareaHTMLAttributes } from 'react';
import { Sparkles } from 'lucide-react';

import { Button } from '../../../../components/ui/Button/Button';
import type {
  ColumnKeyDefinition,
  VariableDefinition,
} from '../variables';
import styles from './VariablePalette.module.css';

export interface VariablePaletteProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  /** Field label rendered above the textarea. */
  label: string;
  /** Current textarea value. */
  value: string;
  /** Update callback — called with the new full value. */
  onChange: (next: string) => void;
  /** Variables shown as chips inside the popup. */
  variables: VariableDefinition[];
  /** Optional second list of column-key chips (used by the body
   *  editor's "label" field; usually omitted for header/footer). */
  columnKeys?: ColumnKeyDefinition[];
  /** Optional aria-label override (for unlabelled fields). */
  ariaLabel?: string;
}

export function VariablePalette({
  label,
  value,
  onChange,
  variables,
  columnKeys,
  ariaLabel,
  rows = 2,
  placeholder,
  maxLength,
  ...rest
}: VariablePaletteProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [open, setOpen] = useState(false);

  function insertToken(token: string) {
    const tag = `{{${token}}}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(tag).catch(() => undefined);
    }
    const ta = textareaRef.current;
    if (ta === null) {
      onChange(`${value}${tag}`);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    const next = `${before}${tag}${after}`;
    onChange(next);
    // Restore focus + cursor position after the inserted tag.
    requestAnimationFrame(() => {
      const fresh = textareaRef.current;
      if (fresh === null) return;
      fresh.focus();
      const cursor = before.length + tag.length;
      fresh.setSelectionRange(cursor, cursor);
    });
  }

  function handleDragStart(token: string) {
    return (e: React.DragEvent<HTMLButtonElement>) => {
      e.dataTransfer.setData('text/plain', `{{${token}}}`);
      e.dataTransfer.effectAllowed = 'copy';
    };
  }

  function handleTextareaDrop(e: React.DragEvent<HTMLTextAreaElement>) {
    const data = e.dataTransfer.getData('text/plain');
    if (data.length === 0) return;
    e.preventDefault();
    const ta = textareaRef.current;
    const start = ta?.selectionStart ?? value.length;
    const end = ta?.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const after = value.slice(end);
    onChange(`${before}${data}${after}`);
  }

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <label className={styles.label}>
          <span>{label}</span>
        </label>
        <Button
          variant="ghost"
          size="sm"
          icon={Sparkles}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={`Show variables for ${label}`}
        >
          Variables
        </Button>
      </div>
      <textarea
        ref={textareaRef}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onDrop={handleTextareaDrop}
        onDragOver={(e) => e.preventDefault()}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={ariaLabel ?? label}
        className={styles.textarea}
        {...rest}
      />
      {open ? (
        <div className={styles.popup} role="region" aria-label="Variable palette">
          <PaletteSection
            heading="Company variables"
            variables={variables.filter((v) => isCompanyToken(v.token))}
            onInsert={insertToken}
            onDragStart={handleDragStart}
          />
          <PaletteSection
            heading="Page-specific variables"
            variables={variables.filter((v) => !isCompanyToken(v.token))}
            onInsert={insertToken}
            onDragStart={handleDragStart}
          />
          {columnKeys !== undefined && columnKeys.length > 0 ? (
            <PaletteSection
              heading="Body column keys"
              variables={columnKeys.map((c) => ({
                token: c.key,
                label: c.label,
                description: `Column key — formats as ${c.format} by default.`,
              }))}
              onInsert={insertToken}
              onDragStart={handleDragStart}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

const COMPANY_TOKENS: ReadonlySet<string> = new Set([
  'company_name',
  'company_logo',
  'company_address',
  'company_phone',
  'company_email',
  'company_subdomain',
  'generated_at',
  'generated_by',
]);

function isCompanyToken(token: string): boolean {
  return COMPANY_TOKENS.has(token);
}

interface PaletteSectionProps {
  heading: string;
  variables: VariableDefinition[];
  onInsert: (token: string) => void;
  onDragStart: (token: string) => (e: React.DragEvent<HTMLButtonElement>) => void;
}

function PaletteSection({
  heading,
  variables,
  onInsert,
  onDragStart,
}: PaletteSectionProps): JSX.Element | null {
  if (variables.length === 0) return null;
  return (
    <div className={styles.section}>
      <h4 className={styles.sectionHead}>{heading}</h4>
      <ul className={styles.chipGrid}>
        {variables.map((v) => (
          <li key={v.token}>
            <button
              type="button"
              className={styles.chip}
              onClick={() => onInsert(v.token)}
              draggable
              onDragStart={onDragStart(v.token)}
              title={v.description}
            >
              <code className={styles.token}>{`{{${v.token}}}`}</code>
              <span className={styles.chipLabel}>{v.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
